"use client"

import { useQueryClient } from "@tanstack/react-query"
import { useState, useEffect } from "react"
import { 
  AprovarFaturaModal, 
  InclusaoFaturasModal, 
  useAnexoFinanceiro, 
  useDeletarFatura, 
  useFaturaByRec, 
  useFaturasStore,
  FilePreviewModal, 
  deleteAnexosByIdentificador,
  AnexoFinanceiroIdentificador,
  useProgressToast
} from "@FN"
import { usePrenotaStore } from "@PN/inclusao"
import { useCatalogs } from "store"
import { 
  Button, 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "visuais"
import { CircleAlert, CircleFadingPlus, EyeIcon, Paperclip, ThumbsUp } from "lucide-react"
import toast from "react-hot-toast"
import { IconEdit, IconTrash } from "@tabler/icons-react"

export function AcoesFatura({ fatura, onAprovar }: { fatura: any, onAprovar?: (fatura: any) => void }) {
  const { showProgressToast } = useProgressToast()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isInclusaoModalOpen, setIsInclusaoModalOpen] = useState(false)
  const [faturaSelecionada, setFaturaSelecionada] = useState<string | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false) 
  const [loadingPreview, setLoadingPreview] = useState(false) 
  const [filesSelecionados, setFilesSelecionados] = useState<Array<{ url: string; name: string; type: string }>>([])
  const [editMode, setEditMode] = useState<"edicao" | "visualizar">("visualizar")

  const queryClient = useQueryClient()
  const { mutateAsync: deletarFatura } = useDeletarFatura()
  const resetPrenota = usePrenotaStore((state) => state.actions.reset)
  
  const { filiais, grupos } = useCatalogs()
  const gruposPermitidos = ["000225", "000003"]
  const podeFinalizar = (grupos.all || []).some(g => gruposPermitidos.includes(g.codigo))
  const podeEditar = fatura.Z15_STATUS !== "Aprovado" && (filiais.all || []).some(f => f.codigo === fatura.Z15_FILIAL)
  
  const { data: faturaAtual } = useFaturaByRec(faturaSelecionada || "")
  
  // Constrói o identificador para buscar anexos
  const identificador: AnexoFinanceiroIdentificador = {
    filial: fatura.Z15_FILIAL,
    fornecedor: fatura.Z15_FORNEC,
    loja: fatura.Z15_LOJA,
    cod: fatura.Z15_COD,
  }
  
  const { data: anexos, refetch } = useAnexoFinanceiro(identificador)

  useEffect(() => {
    if (!faturaAtual || !faturaSelecionada) return

    const faturasMapeadas = faturaAtual[0].titulos.map((t, index) => ({
      id: index.toString(),
      F1_FILIAL: (t.Z16_TITULO || '').substring(0, 4),
      F1_DOC: t.Z16_NF,
      F1_SERIE: t.Z16_SERIE.trim(),
      F1_EMISSAO: t.Z16_EMISSA,
      F1_FORNECE: t.Z16_Z16_FORNENF,
      F1_LOJA: t.Z16_LOJA,
      A2_NOME: faturaAtual[0].A2_NOME,
      VALOR: t.Z16_VALOR,
      F1_CHVNFE: t.Z16_CHAVE,
      Z10_CHAVE: t.Z16_TITULO,
      checked: true,
    }))

    useFaturasStore.getState().setFaturas(faturasMapeadas)

    const fornecedor = {
      A2_COD: faturaAtual[0].Z15_FORNEC,
      A2_LOJA: faturaAtual[0].Z15_LOJA,
      A2_NOME: faturaAtual[0].A2_NOME,
      A2_CGC: faturaAtual[0].A2_CGC,
    }
    useFaturasStore.getState().setFornecedorFatura(fornecedor)
    useFaturasStore.getState().setFornecedores([fornecedor])
    useFaturasStore.getState().setFilial(faturaAtual[0].Z15_FILIAL)
    useFaturasStore.getState().setObservacao(faturaAtual[0].Z15_OBS ?? "")
    useFaturasStore.getState().setRec(Number(faturaAtual[0].REC) || 0)
    
    // Armazena os dados Z15 para uso no modo edição
    useFaturasStore.getState().setFaturaZ15({
      Z15_FILIAL: faturaAtual[0].Z15_FILIAL,
      Z15_FORNEC: faturaAtual[0].Z15_FORNEC,
      Z15_LOJA: faturaAtual[0].Z15_LOJA,
      Z15_COD: faturaAtual[0].Z15_COD,
    })
  }, [faturaAtual, faturaSelecionada])

  const abrirModalAprovar = () => {
    if (onAprovar) {
      onAprovar(fatura) 
    } else {
      resetPrenota()
      setIsModalOpen(true)
    }
  }

  const abrirModalEditar = (rec: string, mode: "edicao" | "visualizar") => {
    useFaturasStore.getState().resetFaturaStore()
    setFaturaSelecionada(rec)
    setEditMode(mode)
    setIsInclusaoModalOpen(true)
  }

  const abrirAnexos = () => {
    setIsPreviewOpen(true)
    setLoadingPreview(true)

    if (anexos && anexos.length > 0) {
      const cached = anexos.map((d) => ({
        url: d.fileData.fileUrl,
        name: d.fileData.fileName,
        type: d.fileData.fileType,
      }))
      setFilesSelecionados(cached)
    } else {
      setFilesSelecionados([])
    }

    refetch()
      .then(({ data }) => {
        if (data && data.length > 0) {
          const mapped = data.map((d) => ({
            url: d.fileData.fileUrl,
            name: d.fileData.fileName,
            type: d.fileData.fileType,
          }))
          setFilesSelecionados(mapped)
        } else if (!anexos || anexos.length === 0) {
          toast.error("Nenhum anexo encontrado")
          setIsPreviewOpen(false)
        }
      })
      .finally(() => {
        setLoadingPreview(false)
      })
  }

  const handleDelete = async () => {
    const progressToast = showProgressToast({
      title: "Excluindo fatura",
      steps: ["Preparando exclusão...", "Removendo anexos...", "Excluindo fatura...", "Atualizando lista..."],
      successMessage: "Fatura e anexos deletados com sucesso!",
    })

    try {
      // Etapa 1: Preparação
      progressToast.updateProgress(0)
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Etapa 2: Deletar anexos
      progressToast.updateProgress(1)
      const identificador: AnexoFinanceiroIdentificador = {
        filial: fatura.Z15_FILIAL,
        fornecedor: fatura.Z15_FORNEC,
        loja: fatura.Z15_LOJA,
        cod: fatura.Z15_COD,
      }
      await deleteAnexosByIdentificador(identificador)

      // Etapa 3: Deletar fatura
      progressToast.updateProgress(2)
      await deletarFatura({ Z15_COD: fatura.Z15_COD, FILIAL: fatura.Z15_FILIAL })

      // Etapa 4: Atualizar lista
      progressToast.updateProgress(3)
      queryClient.invalidateQueries({ queryKey: ["financeiro", "faturas"] })

      // Finalizar com sucesso
      progressToast.updateProgress(3)
    } catch (e) {
      console.error(e)
      progressToast.markAsError("Erro ao excluir fatura. Tente novamente.")
    }
  }
  
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Abrir menu</span>
            <CircleFadingPlus className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Fatura {fatura.Z15_COD}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            disabled={!podeFinalizar}
            onClick={() => abrirModalEditar(fatura.R_E_C_N_O_, "visualizar")}
          >
            <EyeIcon className="text-indigo-300 mr-2 size-4"/>
            Visualizar
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={fatura.Z15_STATUS === "Aprovado" || !podeFinalizar}
            onClick={abrirModalAprovar}
          >
            <ThumbsUp className="text-emerald-500 mr-2 size-4" />
            Aprovar
          </DropdownMenuItem>
          <DropdownMenuItem disabled={fatura.Z15_STATUS === "Aprovado" || true}>
            <CircleAlert className="text-amber-500 mr-2 size-4" />
            Revisar
          </DropdownMenuItem>
          <DropdownMenuItem onClick={abrirAnexos}>
            <Paperclip className="text-slate-500 mr-2 size-4" />
            Anexo
          </DropdownMenuItem>
          <DropdownMenuItem 
            disabled={!podeEditar} 
            onClick={() => abrirModalEditar(fatura.R_E_C_N_O_, "edicao")}
          >
            <IconEdit className="text-cyan-500 mr-2 size-4" />
            Editar
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={fatura.Z15_STATUS === "Aprovado"}
            onClick={handleDelete}
          >
            <IconTrash className="text-destructive mr-2 size-4" />
            Excluir
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AprovarFaturaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fatura={fatura}
      />

      {isInclusaoModalOpen && (
        <InclusaoFaturasModal
          isOpen={isInclusaoModalOpen}
          onClose={() => {
            setIsInclusaoModalOpen(false)
            useFaturasStore.getState().resetFaturaStore()
            setFaturaSelecionada(null)
            queryClient.invalidateQueries({ queryKey: ["financeiro", "faturas"] })
          }}
          mode={editMode || "visualizar"}
        />
      )}

      {filesSelecionados.length > 0 && (
        <FilePreviewModal
          files={filesSelecionados}
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          loading={loadingPreview}
        />
      )}
    </>
  )
}
